import { ZuiBaseElement } from '@zywave/zui-base';
import { property } from 'lit/decorators.js';
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { style } from './zui-progress-css.js';
import { updateCustomState } from '@zywave/zui-base/dist/utils/update-custom-state.js';
import type { PropertyValues } from 'lit';

/**
 * Represents a linear progress indicator.
 * @element zui-progress
 *
 * @cssprop [--zui-progress-color=var(--zui-blue)] - Set the primary color of the progress bar
 * @cssprop [--zui-progress-duration=1.5s] - Set the progress bar animation duration
 *
 * @cssState indeterminate - Applied when the progress bar is in indeterminate state
 */
export class ZuiProgress extends ZuiBaseElement {
  /**
   * Sets the progress bar into its indeterminate state
   */
  @property({ type: Boolean })
  indeterminate = false;

  /**
   * Sets the primary progress bar's value. Value should be between `[0, 100]`.
   */
  @property({ type: Number })
  progress = 0;

  #internals?: ElementInternals;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();

    this.#internals = this.attachInternals?.();
  }

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', '100');
  }

  updated(changedProps: PropertyValues) {
    super.updated(changedProps);

    if (changedProps.has('indeterminate')) {
      this.indeterminate
        ? updateCustomState(this.#internals, 'add', 'indeterminate')
        : updateCustomState(this.#internals, 'delete', 'indeterminate');
    }
  }

  render() {
    const progress: number | undefined = !this.indeterminate ? Math.min(this.progress, 100) : undefined;
    const progressStyles: { [cssProp: string]: string } = {};

    if (progress !== undefined) {
      this.setAttribute('aria-valuenow', progress.toString());
    } else {
      this.removeAttribute('aria-valuenow');
    }

    if (!this.indeterminate) {
      progressStyles.transform = `scaleX(${progress / 100})`;
    }
    return html`
      <div class="container">
        <div class="track-bar"></div>
        <div
          style="${styleMap(progressStyles)}"
          class="progress-bar ${classMap({ indeterminate: this.indeterminate })}"
        ></div>
      </div>
    `;
  }
}

window.customElements.define('zui-progress', ZuiProgress);

declare global {
  interface HTMLElementTagNameMap {
    'zui-progress': ZuiProgress;
  }
}
